import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={ref}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/eae0e2bc78e43d9b41c054c86964ee08.jpg"
          alt="Worship service with LED screens"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-black/70 via-brand-black/55 to-brand-black/80" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08)_0%,transparent_70%)]" />

      {/* Content */}
      <div
        className={`relative z-10 w-full text-center px-4 md:px-8 max-w-5xl mx-auto pt-16 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-gold/50 bg-gold/10 rounded-full px-4 py-1.5 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-gold block" />
          <span className="font-body text-xs text-gold tracking-widest uppercase">{t('hero.badge')}</span>
        </div>

        {/* Main Headline */}
        <h1 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl text-brand-white leading-tight mb-6">
          {t('hero.headline1')}
          <br />
          <span className="text-gold">{t('hero.headline2')}</span>
        </h1>

        {/* Subheadline */}
        <p className="font-body text-base md:text-xl text-brand-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
          {t('hero.sub')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <button
            onClick={() => scrollTo('contact')}
            className="font-heading font-bold text-sm md:text-base bg-gold text-brand-black px-8 py-4 rounded-full hover:bg-gold-light transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            style={{ boxShadow: '0 0 30px rgba(212,175,55,0.4)' }}
          >
            {t('hero.cta1')}
          </button>
          <button
            onClick={() => scrollTo('benefits')}
            className="font-heading font-semibold text-sm md:text-base border border-brand-white/40 text-brand-white px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            {t('hero.cta2')}
          </button>
        </div>

        {/* Financing Highlight */}
        <div className="flex items-center justify-center mb-10 px-2">
          <div className="flex flex-col sm:flex-row items-center gap-3 bg-gold/15 border border-gold/40 rounded-2xl px-5 py-4 backdrop-blur-sm w-full sm:w-auto max-w-lg">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold shrink-0">
                <i className="ri-percent-line text-base text-brand-black" />
              </div>
              <div className="text-left">
                <p className="font-heading font-bold text-sm md:text-base text-gold leading-tight">
                  {t('hero.financing')}
                </p>
                <p className="font-body text-xs text-brand-white/60 mt-0.5">
                  {t('hero.financing.sub')}
                </p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-gold/30 shrink-0" />
            <div className="flex items-center gap-4 sm:gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-bank-line text-sm text-gold/70" />
                </div>
                <span className="font-body text-xs text-brand-white/60 whitespace-nowrap">{t('hero.financing.nobank')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-sm text-gold/70" />
                </div>
                <span className="font-body text-xs text-brand-white/60 whitespace-nowrap">{t('hero.financing.approved')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="flex flex-col items-center gap-2 opacity-60">
          <span className="font-body text-xs text-brand-white/60 tracking-widest uppercase">{t('hero.scroll')}</span>
          <div className="w-px h-12 bg-gradient-to-b from-gold/60 to-transparent" />
        </div>
      </div>
    </section>
  );
}
